<template>
  <div class="tw-max-h-full">
    <GraphqlDataTable
      v-model:selection-mode="selectionMode"
      :query="query"
      :max-height-adjustment="30"
      :data-test="dataTest"
      :additional-query-variables="{ batchIds: batchIds }"
      :order-by="initialOrder"
      :enabled-row-selection-modes="enabledRowSelectionModes"
      v-bind="$attrs"
      :filter-black-list="['accounts']"
      class="eor-payment-requests-table"
    >
      <template v-if="$slots.action" #action="{ row }">
        <slot name="action" :row="row" />
      </template>
      <template v-if="$slots.filters" #filters>
        <slot name="filters" />
      </template>
    </GraphqlDataTable>

    <WdsDrawer
      v-model:show="partnerWorkerDetailsSidebarVisible"
      position="right"
      dismissible
      width="420px"
      padding="0"
    >
      <PartnerWorkerDetailPanel
        :worker-id="partnerWorkerDetailsProps.workerId"
        :employment-id="partnerWorkerDetailsProps.employmentId"
        @close="closePartnerDetails"
      />
    </WdsDrawer>
  </div>
</template>

<script lang="ts" setup>
import { reactive, ref, watch } from 'vue'
import { TypedQueryDocumentNode } from 'graphql/index'
import { WdsDrawer } from '@worksome/wds-components'
import { PaymentRequestOrderByColumn, SortOrder } from '@/gql/graphql'
import GraphqlDataTable from '@/components/GraphqlDataTable/GraphqlDataTable.vue'
import { RowSelectionMode } from '@/components/GraphqlDataTable/enums'
import PartnerWorkerDetailPanel from '@/components/WorkerDetailPanel/PartnerWorkerDetailPanel.vue'
import {
  addOpenPartnerWorkerDetailPanelListener,
  removeOpenPartnerWorkerDetailPanelListener,
  OpenPartnerWorkerDetailPanelInput,
} from '@/components/GraphqlDataTable/listners'

const props = withDefaults(
  defineProps<{
    query: TypedQueryDocumentNode
    dataTest?: string
    batchIds?: string[]
    enabledRowSelectionModes?: RowSelectionMode[]
    isActiveTab?: boolean
  }>(),
  {
    batchIds: () => [],
    dataTest: undefined,
    enabledRowSelectionModes: () => [RowSelectionMode.PAGE],
  },
)

const initialOrder = {
  field: PaymentRequestOrderByColumn.IssuedAt,
  order: SortOrder.Desc,
}

const selectionMode = defineModel<RowSelectionMode>('selectionMode', {
  default: RowSelectionMode.PAGE,
})

// Partner worker details state and listener
const partnerWorkerDetailsSidebarVisible = ref(false)
const partnerWorkerDetailsProps = reactive<{
  workerId: string
  employmentId?: string
}>({
  workerId: '',
  employmentId: undefined,
})

const closePartnerDetails = () => {
  partnerWorkerDetailsProps.workerId = ''
  partnerWorkerDetailsProps.employmentId = undefined
  partnerWorkerDetailsSidebarVisible.value = false
}

const openPartnerWorkerDetailPanel = (input: OpenPartnerWorkerDetailPanelInput) => {
  partnerWorkerDetailsProps.workerId = input.workerId
  partnerWorkerDetailsProps.employmentId = input.employmentId
  partnerWorkerDetailsSidebarVisible.value = true
}

// Auto-close when tab becomes inactive
watch(
  () => props.isActiveTab,
  (active) => {
    if (active) {
      addOpenPartnerWorkerDetailPanelListener(openPartnerWorkerDetailPanel)
    } else {
      removeOpenPartnerWorkerDetailPanelListener(openPartnerWorkerDetailPanel)
      closePartnerDetails()
    }
  },
)

// Initialize listener based on initial tab state
if (props.isActiveTab) {
  addOpenPartnerWorkerDetailPanelListener(openPartnerWorkerDetailPanel)
}
</script>

<style scoped>
/* Fix EoR ActionBar spacing and width */
:deep(.eor-payment-requests-table) {
  .tw--translate-x-1\/2 {
    width: 800px !important;

    .tw-w-\[800px\] {
      width: 100% !important;
      max-width: 100% !important;
      justify-content: space-between !important;
      display: flex !important;

      /* Button styling and dividers */
      > div:last-child {
        gap: 1rem !important; /* Override gap-lg with gap-md equivalent */

        /* Add dividers between buttons */
        > div:not(:first-child)::before {
          content: '';
          position: absolute;
          left: -0.5rem;
          top: 50%;
          transform: translateY(-50%);
          width: 1px;
          height: 1.5rem; /* tw-h-6 */
          background-color: #e5e7eb; /* gray-200 */
        }

        /* Position button containers relatively for divider positioning */
        > div:not(:first-child) {
          position: relative;
        }

        /* Style "Confirm payrolled" button green */
        button[data-test="confirm-payrolled-bulk"] {
          color: #1E6021 !important; /* green-600 */

          &:hover {
            color: #047E43 !important; /* green-500 - lighter on hover like other success buttons */
          }
        }
      }
    }
  }
}
</style>
